import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useState } from "react";
import { Check, Loader2, CalendarIcon, Sparkles } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { SERVICES, SERVICE_BY_ID } from "@/lib/business";
import { supabase } from "@/integrations/supabase/client";
import { LazyCalendar as Calendar } from "@/components/ui/calendar-lazy";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Autocomplete, AddressAutocomplete } from "@/components/Autocomplete";
import { VEHICLE_MAKES, VEHICLE_YEARS, getModelsForMake } from "@/lib/vehicles";
import { buildBreadcrumbJsonLd, canonicalLink, pageSocialMeta } from "@/lib/seo";

const BREADCRUMB_JSONLD = buildBreadcrumbJsonLd([
  { name: "Home", path: "/" },
  { name: "Book", path: "/book" },
]);

const schema = z.object({
  name: z.string().trim().min(2, "Required").max(100),
  email: z.string().trim().email("Invalid email").max(255),
  phone: z.string().trim().min(10, "Valid phone required").max(25),
  vehicle_make: z.string().trim().min(1, "Required").max(50),
  vehicle_model: z.string().trim().min(1, "Required").max(50),
  vehicle_year: z.preprocess((v) => Number(v), z.number().int().min(1900).max(Math.max(2026, new Date().getFullYear() + 1))) as unknown as z.ZodNumber,
  service: z.enum(["basic_wash", "full_detail", "premium_package"]),
  scheduled_date: z.string().min(1, "Required"),
  scheduled_time: z.string().min(1, "Required"),
  service_address: z.string().trim().min(5, "Full address required").max(255),
  notes: z.string().max(500).optional(),
  sms_consent: z.literal(true, { message: "SMS consent is required to book" }),
});

type FormData = z.infer<typeof schema>;

export const Route = createFileRoute("/book")({
  validateSearch: (s: Record<string, unknown>): { service?: string } => ({
    service: typeof s.service === "string" && s.service ? s.service : undefined,
  }),
  head: () => {
    const title = "Book Mobile Car Detailing NYC | BLVDS Detailing";
    const description = "Book mobile car detailing in NYC online in 60 seconds. Same-week appointments across Queens, Brooklyn & Long Island — we come to your driveway.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        ...pageSocialMeta("/book", title, description),
      ],
      links: [canonicalLink("/book")],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(BREADCRUMB_JSONLD) },
      ],
    };
  },
  component: BookPage,
});

function BookPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const [done, setDone] = useState<{ ref: string; service: string } | null>(null);

  const today = new Date().toISOString().split("T")[0];

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      service: (search.service as any) || undefined,
      scheduled_date: today,
      scheduled_time: "10:00",
    },
  });

  const selectedService = form.watch("service");

  async function onSubmit(values: FormData) {
    const svc = SERVICE_BY_ID[values.service];
    const scheduled_at = new Date(`${values.scheduled_date}T${values.scheduled_time}:00`).toISOString();

    try {
      const { data: appointmentId, error } = await supabase.rpc("create_booking", {
        _name: values.name,
        _email: values.email,
        _phone: values.phone,
        _address: values.service_address,
        _vehicle_make: values.vehicle_make,
        _vehicle_model: values.vehicle_model,
        _vehicle_year: values.vehicle_year,
        _service: values.service,
        _service_label: svc.name,
        _price_cents: svc.priceCents,
        _scheduled_at: scheduled_at,
        _service_address: values.service_address,
        _vehicle_snapshot: `${values.vehicle_year} ${values.vehicle_make} ${values.vehicle_model}`,
        _notes: values.notes ?? "",
      });

      if (error || !appointmentId) {
        console.error("Booking error:", error);
        toast.error("Couldn't save your appointment. Try again.");
        return;
      }
      // Fire-and-forget business notification email (best effort)
      void fetch("/api/public/booking-notify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ appointmentId }),
      }).catch((e) => console.error("Email send failed:", e));

      setDone({ ref: String(appointmentId).slice(0, 8).toUpperCase(), service: svc.name });
      
    } catch (e) {
      console.error(e);
      toast.error("Something went wrong. Try again.");
    }
  }

  if (done) {
    return (
      <Layout>
        <section className="min-h-[70vh] flex items-center max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div>
            <div className="w-20 h-20 rounded-full bg-gradient-primary mx-auto flex items-center justify-center mb-6 shadow-glow">
              <Check className="w-10 h-10 text-primary-foreground" strokeWidth={3} />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">Booking received!</h1>
            <p className="text-lg text-muted-foreground mb-8">
              Thanks we've got your <span className="text-foreground font-semibold">{done.service}</span> request. We'll call to confirm within 2 hours.
            </p>
            <div className="inline-block px-5 py-3 rounded-lg bg-surface border border-border mb-8">
              <span className="text-sm text-muted-foreground">Reference: </span>
              <span className="font-mono font-bold text-primary">#{done.ref}</span>
            </div>
            <div>
              <button
                onClick={() => navigate({ to: "/" })}
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-gradient-primary text-primary-foreground font-semibold"
              >
                Back to home
              </button>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="pt-16 pb-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <p className="text-sm font-semibold text-primary uppercase tracking-wider mb-3">Book Online</p>
        <h1 className="text-5xl sm:text-6xl font-bold mb-5">Book your <span className="text-gradient-primary">detail</span>.</h1>
        <p className="text-lg text-muted-foreground">Takes 60 seconds. We'll call to confirm.</p>
      </section>

      <section className="pb-20 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 p-6 sm:p-8 rounded-2xl bg-surface border border-border">
          {/* Service */}
          <div>
            <label className="text-sm font-semibold mb-3 block">Choose your service</label>
            <div className="grid sm:grid-cols-3 gap-3">
              {SERVICES.map((s) => (
                <label
                  key={s.id}
                  className={`relative cursor-pointer p-4 rounded-xl border-2 transition-smooth ${
                    selectedService === s.id
                      ? "border-primary bg-primary/10"
                      : "border-border bg-background hover:border-primary/30"
                  }`}
                >
                  <input type="radio" value={s.id} {...form.register("service")} className="sr-only" />
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-bold">{s.name}</span>
                    {selectedService === s.id && <Sparkles className="w-4 h-4 text-primary" />}
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">{s.duration}</p>
                  <p className="text-2xl font-bold text-gradient-primary">${s.price}</p>
                </label>
              ))}
            </div>
          </div>

          <Field id="bk-name" label="Full Name" error={form.formState.errors.name?.message}>
            <input id="bk-name" {...form.register("name")} className={inputCls} placeholder="Jane Driver" />
          </Field>

          <div className="grid sm:grid-cols-2 gap-5">
            <Field id="bk-email" label="Email" error={form.formState.errors.email?.message}>
              <input id="bk-email" type="email" {...form.register("email")} className={inputCls} placeholder="you@example.com" />
            </Field>
            <Field id="bk-phone" label="Phone" error={form.formState.errors.phone?.message}>
              <input id="bk-phone" type="tel" {...form.register("phone")} className={inputCls} placeholder="(555) 123-4567" />
            </Field>
          </div>

          <div className="grid sm:grid-cols-3 gap-5">
            <Field id="bk-make" label="Vehicle Make" error={form.formState.errors.vehicle_make?.message}>
              <Controller
                control={form.control}
                name="vehicle_make"
                render={({ field }) => (
                  <Autocomplete
                    id="bk-make"
                    value={field.value ?? ""}
                    onChange={(v) => {
                      field.onChange(v);
                      if (form.getValues("vehicle_model")) form.setValue("vehicle_model", "");
                    }}
                    suggestions={VEHICLE_MAKES}
                    placeholder="BMW"
                    className={inputCls}
                    filter={(q, items) => {
                      const sorted = [...items].sort((a, b) => a.localeCompare(b));
                      if (!q) return sorted;
                      const lower = q.toLowerCase();
                      return sorted.filter((i) => i.toLowerCase().includes(lower));
                    }}
                  />
                )}
              />
            </Field>
            <Field id="bk-model" label="Model" error={form.formState.errors.vehicle_model?.message}>
              <Controller
                control={form.control}
                name="vehicle_model"
                render={({ field }) => {
                  const make = form.watch("vehicle_make");
                  const models = getModelsForMake(make ?? "");
                  return (
                    <Autocomplete
                      id="bk-model"
                      value={field.value ?? ""}
                      onChange={field.onChange}
                      suggestions={models}
                      placeholder={make ? "M3" : "Pick make first"}
                      className={inputCls}
                    />
                  );
                }}
              />
            </Field>
            <Field id="bk-year" label="Year" error={form.formState.errors.vehicle_year?.message}>
              <Controller
                control={form.control}
                name="vehicle_year"
                render={({ field }) => (
                  <Autocomplete
                    id="bk-year"
                    value={field.value ? String(field.value) : ""}
                    onChange={(v) => field.onChange(v === "" ? undefined : Number(v))}
                    suggestions={VEHICLE_YEARS.map(String)}
                    placeholder="2022"
                    className={inputCls}
                    inputMode="numeric"
                    filter={(q, items) => (q ? items.filter((i) => i.startsWith(q)) : items)}
                  />
                )}
              />
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <Field id="bk-date" label="Preferred Date" error={form.formState.errors.scheduled_date?.message}>
              <Controller
                control={form.control}
                name="scheduled_date"
                render={({ field }) => {
                  const selected = field.value ? new Date(`${field.value}T00:00:00`) : undefined;
                  return (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          id="bk-date"
                          type="button"
                          className={`${inputCls} flex items-center gap-2 text-left`}
                        >
                          <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                          <span className={selected ? "" : "text-muted-foreground"}>
                            {selected ? format(selected, "PPP") : "Pick a date"}
                          </span>
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 z-50" align="start">
                        <Calendar
                          mode="single"
                          selected={selected}
                          onSelect={(d) => {
                            if (d) field.onChange(format(d, "yyyy-MM-dd"));
                          }}
                          disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                          captionLayout="dropdown"
                          initialFocus
                          className={cn("p-3 pointer-events-auto")}
                        />
                      </PopoverContent>
                    </Popover>
                  );
                }}
              />
            </Field>
            <Field id="bk-time" label="Preferred Time" error={form.formState.errors.scheduled_time?.message}>
              <select id="bk-time" {...form.register("scheduled_time")} className={inputCls}>
                {["08:00","09:00","10:00","11:00","12:00","13:00","14:00","15:00","16:00","17:00"].map(t => (
                  <option key={t} value={t}>{formatTime(t)}</option>
                ))}
              </select>
            </Field>
          </div>

          <Field id="bk-address" label="Service Address (where we'll come)" error={form.formState.errors.service_address?.message}>
            <Controller
              control={form.control}
              name="service_address"
              render={({ field }) => (
                <AddressAutocomplete
                  id="bk-address"
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  placeholder="123 Main St, Queens, NY 11375"
                  className={inputCls}
                />
              )}
            />
          </Field>

          <Field id="bk-notes" label="Notes (optional)" error={form.formState.errors.notes?.message}>
            <textarea id="bk-notes" {...form.register("notes")} rows={3} className={inputCls} placeholder="Gate code, parking notes, areas of concern..." />
          </Field>

          <div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                {...form.register("sms_consent")}
                className="mt-1 w-4 h-4 rounded border-border accent-primary cursor-pointer flex-shrink-0"
              />
              <span className="text-sm text-muted-foreground leading-relaxed">
                I agree to receive SMS booking confirmations and appointment reminders from BLVDS Detailing. Message and data rates may apply. Reply STOP to unsubscribe.
              </span>
            </label>
            {form.formState.errors.sms_consent?.message && (
              <p className="text-xs text-destructive mt-1.5">{form.formState.errors.sms_consent.message as string}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={form.formState.isSubmitting}
            className="w-full py-4 rounded-xl bg-gradient-primary text-primary-foreground font-bold text-lg shadow-glow hover:scale-[1.01] transition-smooth disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {form.formState.isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Confirm Booking"}
          </button>
        </form>
      </section>
    </Layout>
  );
}

const inputCls =
  "w-full min-h-12 px-4 py-3 rounded-lg bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/30 outline-none transition-smooth text-foreground placeholder:text-muted-foreground";

function Field({ id, label, error, children }: { id?: string; label: string; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <label htmlFor={id} className="text-sm font-semibold mb-2 block">{label}</label>
      {children}
      {error && <p className="text-xs text-destructive mt-1.5">{error}</p>}
    </div>
  );
}

function formatTime(t: string) {
  const [h] = t.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hour = h % 12 || 12;
  return `${hour}:00 ${period}`;
}
